import * as React from 'react';
import { Component } from 'react';
import { action, computed, toJS } from 'mobx';
import { observer, inject } from 'mobx-react';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import groupBy from 'lodash/groupBy';
import FundCard from './FundCard';
import FundDocuments from './FundDocuments';
import formatter from '../utils/formatter';
import getDataPoint from '../utils/getDataPoint';
import { performanceCheck as checkPerformance } from '../utils/performanceCheck';
import { stripHtml, sortString, sortNumber } from '../utils';
import { NO_DOCUMENTS_MESSAGE } from '../constants';
import { getDisclaimers } from '../utils/disclaimers';
import getConditionalData from '../utils/query/getConditionalData';
import getConditionalDataFlag from '../utils/query/getConditionalDataFlag';
import normalizeQueryResults from '../utils/query/normalizeQueryResults';
import { replacePlaceholders } from '../utils/replacePlaceholders';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import { DataStore } from '../stores';
import { ShareClass } from '../models/entity';
import { utils } from '@kurtosys/ksys-app-template';

// Higher-order component to return a shareclass row/card with data and methods required to function
const withShareclass = WrappedComponent => {
    @inject('dataStore')
    @observer
    class Shareclass extends Component<any, any> {
        @computed
        get expanded() {
            const { shareClass, dataStore } = this.props;
            return dataStore.getExpandStatus(shareClass);
        }

        @computed
        get noDataPlaceholder() {
            return this.props.dataStore.config.noDataPlaceholder;
        }

        @computed
        get hasPerformance() {
            const { dataStore, shareClass } = this.props;
            const { config } = dataStore;
            return checkPerformance({
                dataStore,
                config,
                shareClass,
            });
        }

        @computed
        get selectedDocumentsLabel() {
            const { selectedDocumentsLabel } = this.props.dataStore.config;
            return selectedDocumentsLabel;
        }

        @computed
        get availableDocumentsLabel() {
            const { availableDocumentsLabel } = this.props.dataStore.config;
            return availableDocumentsLabel;
        }

        @computed
        get groupedDocuments() {
            const {
                documentGroupMeta,
                cultureAliases,
                documentGroupOrder,
            } = this.props.dataStore.config;
            const groupedDocuments = groupBy(
                this.props.shareClass.documents,
                `meta.${documentGroupMeta}.value`
            );

            return Object.keys(groupedDocuments)
                .sort(sortString)
                .map(documentGroupName => {
                    const documentGroupNameIndex = documentGroupOrder.indexOf(
                        documentGroupName
                    );
                    return {
                        order:
                            documentGroupNameIndex > -1
                                ? documentGroupNameIndex
                                : documentGroupOrder.length,
                        documentGroupName,
                        documents: groupedDocuments[documentGroupName].sort(
                            (a, b) =>
                                sortString(
                                    get(
                                        cultureAliases,
                                        a.cultureCode,
                                        a.cultureCode
                                    ),
                                    get(
                                        cultureAliases,
                                        b.cultureCode,
                                        b.cultureCode
                                    )
                                )
                        ),
                    };
                })
                .sort((a, b) => sortNumber(a.order, b.order));
        }

        isValueDisabled(
            dataStore: DataStore,
            shareClass: ShareClass,
            disabledEntityCheck: boolean
        ) {
            if (disabledEntityCheck) {
                const disabledEntityFlag = dataStore.config.disabledEntityFlag;

                if (
                    disabledEntityFlag &&
                    disabledEntityFlag.conditional &&
                    utils.isArrayLike.isArrayLike(disabledEntityFlag.queries) &&
                    disabledEntityFlag.queries.length > 0
                ) {
                    const values = normalizeQueryResults({
                        ...shareClass.properties_pub,
                        ...getConditionalData(shareClass, disabledEntityFlag),
                    });
                    return getConditionalDataFlag(values, disabledEntityFlag);
                }
            }
            return false;
        }

        @computed
        get columns() {
            const { dataStore, shareClass } = this.props;

            const values = this.props.columns.map(column => {
                const {
                    type,
                    property,
                    data,
                    columns,
                    groups,
                    disabledEntityCheck,
                } = column;

                const emptyResponse = col => ({
                    ...col,
                    data: {
                        value: col.noDataPlaceholder || this.noDataPlaceholder,
                    },
                });

                // GROUPED COLUMNS
                if (type === 'group' && columns.length > 0) {
                    return {
                        ...column,
                        values: columns.map(c => {
                            if (
                                this.isValueDisabled(
                                    dataStore,
                                    shareClass,
                                    c.disabledEntityCheck
                                )
                            ) {
                                return emptyResponse(c);
                            }

                            if (isArray(toJS(c.data))) {
                                return {
                                    ...c,
                                    multiple: true,
                                    data: c.data.map(d => ({
                                        ...d,
                                        value: getDataPoint({
                                            dataStore,
                                            shareClass,
                                            dataConfig: d,
                                            locale: this.locale,
                                            fundCurrency: this.fundCurrency,
                                            defaultNoDataPlaceholder:
                                                c.noDataPlaceholder ||
                                                this.noDataPlaceholder,
                                            hasPerformance: this.hasPerformance,
                                        }),
                                    })),
                                };
                            }

                            if (c.data) {
                                return {
                                    ...c,
                                    data: {
                                        ...c.data,
                                        value: getDataPoint({
                                            dataStore,
                                            shareClass,
                                            dataConfig: c.data,
                                            locale: this.locale,
                                            fundCurrency: this.fundCurrency,
                                            defaultNoDataPlaceholder:
                                                c.noDataPlaceholder ||
                                                this.noDataPlaceholder,
                                            hasPerformance: this.hasPerformance,
                                        }),
                                    },
                                };
                            }
                        }),
                    };
                }

                if (type === 'dropdown' && groups.length > 0) {
                    return {
                        ...column,
                        groups: groups.map(g => ({
                            ...g,
                            values: g.columns.map(c => {
                                if (
                                    this.isValueDisabled(
                                        dataStore,
                                        shareClass,
                                        c.disabledEntityCheck
                                    )
                                ) {
                                    return emptyResponse(c);
                                }

                                if (isArray(toJS(c.data))) {
                                    return {
                                        ...c,
                                        multiple: true,
                                        data: c.data.map(d => ({
                                            ...d,
                                            value: getDataPoint({
                                                dataStore,
                                                shareClass,
                                                dataConfig: d,
                                                locale: this.locale,
                                                fundCurrency: this.fundCurrency,
                                                defaultNoDataPlaceholder:
                                                    c.noDataPlaceholder ||
                                                    this.noDataPlaceholder,
                                                hasPerformance: this
                                                    .hasPerformance,
                                            }),
                                        })),
                                    };
                                }

                                if (c.data) {
                                    return {
                                        ...c,
                                        data: {
                                            ...c.data,
                                            value: getDataPoint({
                                                dataStore,
                                                shareClass,
                                                dataConfig: c.data,
                                                locale: this.locale,
                                                fundCurrency: this.fundCurrency,
                                                defaultNoDataPlaceholder:
                                                    c.noDataPlaceholder ||
                                                    this.noDataPlaceholder,
                                                hasPerformance: this
                                                    .hasPerformance,
                                            }),
                                        },
                                    };
                                }
                            }),
                        })),
                    };
                }

                // NON-GROUPED COLUMNS
                if (
                    this.isValueDisabled(
                        dataStore,
                        shareClass,
                        disabledEntityCheck
                    )
                ) {
                    return emptyResponse(column);
                }

                if (type === 'document') {
                    return {
                        ...column,
                        value: this.getDocuments(
                            column.documentType,
                            column.performanceCheck,
                            column.noDataPlaceholder
                        ),
                    };
                }

                if (isArray(toJS(data))) {
                    return {
                        ...column,
                        multiple: true,
                        data: data.map(d => {
                            return {
                                ...d,
                                value: getDataPoint({
                                    dataStore,
                                    shareClass,
                                    dataConfig: d,
                                    locale: this.locale,
                                    fundCurrency: this.fundCurrency,
                                    defaultNoDataPlaceholder:
                                        column.noDataPlaceholder ||
                                        this.noDataPlaceholder,
                                    hasPerformance: this.hasPerformance,
                                }),
                            };
                        }),
                    };
                }

                if (data) {
                    return {
                        ...column,
                        data: {
                            ...data,
                            value: getDataPoint({
                                dataStore,
                                shareClass,
                                dataConfig: data,
                                locale: this.locale,
                                fundCurrency: this.fundCurrency,
                                defaultNoDataPlaceholder:
                                    column.noDataPlaceholder ||
                                    this.noDataPlaceholder,
                                hasPerformance: this.hasPerformance,
                            }),
                        },
                    };
                }

                // Support simple label/property column definition
                if (property) {
                    return {
                        ...column,
                        data: {
                            ...data,
                            value: getDataPoint({
                                dataStore,
                                shareClass,
                                dataConfig: {
                                    property: 'properties_pub',
                                    code: property,
                                    key: 'value',
                                    performanceCheck: column.performanceCheck,
                                },
                                locale: this.locale,
                                fundCurrency: this.fundCurrency,
                                defaultNoDataPlaceholder:
                                    column.noDataPlaceholder ||
                                    this.noDataPlaceholder,
                                hasPerformance: this.hasPerformance,
                            }),
                        },
                    };
                }

                return emptyResponse(column);
            });

            return values;
        }

        getDocuments = (
            documentType,
            performanceCheck,
            noDataPlaceholder = this.noDataPlaceholder
        ) => {
            if (performanceCheck && !this.hasPerformance) {
                return noDataPlaceholder;
            }

            const { documents = [] } =
                this.groupedDocuments.find(
                    d => d.documentGroupName === documentType
                ) || {};

            return documents;
        };

        documentSelected = clientCode => {
            return (
                this.props.documentDownloads.findIndex(
                    document => document.clientCode === clientCode
                ) > -1
            );
        };

        getPriceHistory = columnConfig => {
            const { dataStore } = this.props;
            const { data, sort, columns, disclaimerTypes } = columnConfig;

            const headings = columns.map(c => `"${c.label}"`).join(',');

            const dataSet = getDataPoint({
                dataStore,
                dataConfig: { ...data, sort },
                shareClass: this.props.shareClass,
                locale: this.locale,
                fundCurrency: this.fundCurrency,
                defaultNoDataPlaceholder: this.noDataPlaceholder,
                hasPerformance: this.hasPerformance,
            });

            const disclaimers = this.disclaimerByType(disclaimerTypes)
                .map(d => `"${stripHtml(d)}"`)
                .join('\n');

            let csv = dataSet.map(d => {
                return columns
                    .map(column => {
                        let columnValue;
                        if (column.data) {
                            columnValue = getDataPoint({
                                dataStore,
                                dataConfig: column.data,
                                shareClass: this.props.shareClass,
                                locale: this.locale,
                                fundCurrency: this.fundCurrency,
                                defaultNoDataPlaceholder: this
                                    .noDataPlaceholder,
                                hasPerformance: this.hasPerformance,
                            });
                        }
                        if (column.dataKey) {
                            const value = get(
                                d,
                                column.dataKey,
                                this.noDataPlaceholder
                            );
                            columnValue = column.format
                                ? formatter({
                                      dataStore,
                                      value,
                                      type: column.format.type,
                                      locale: this.locale,
                                      currency: this.fundCurrency,
                                      options: column.format.options,
                                  })
                                : value;
                        }
                        return `"${columnValue}"`;
                    })
                    .join(',');
            });

            csv.unshift(headings);
            csv.push(disclaimers);
            csv = csv.join('\n');
            const filename = this.getDownloadFilename(columnConfig);
            this.props.dataStore.downloadCsv(csv, filename);
        };

        getDownloadFilename = (
            columnConfig: ColumnConfig,
            extension: string = '.csv'
        ) => {
            const { dataStore } = this.props;
            const defaultFilename = 'historic_prices';
            let response = `${defaultFilename}${extension}`;
            const { downloadFilename } = columnConfig;
            if (downloadFilename) {
                const { text, dataFields } = downloadFilename;
                const data = Object.keys(dataFields || {}).reduce(
                    (acc, key) => {
                        const value = dataFields[key];
                        const { dataLocator, format } = value;
                        acc[key] = getDataPoint({
                            dataStore,
                            dataConfig: dataLocator,
                            shareClass: this.props.shareClass,
                            locale: this.locale,
                            fundCurrency: this.fundCurrency,
                            defaultNoDataPlaceholder: this.noDataPlaceholder,
                            hasPerformance: this.hasPerformance,
                        });
                        if (acc[key] && format) {
                            acc[key] = formatter({
                                dataStore,
                                type: format.type,
                                locale: this.locale,
                                currency: this.fundCurrency,
                                options: format.options,
                                value: acc[key],
                            });
                        }
                        return acc;
                    },
                    {}
                );
                response = [
                    replacePlaceholders({
                        values: data,
                        text: `${text || defaultFilename}`,
                    }),
                    extension,
                ].join('');
            }
            return response;
        };

        @computed
        get shareClassDownloads() {
            return this.props.shareClass.documents.filter(document =>
                this.documentSelected(document.clientCode)
            );
        }

        @computed
        get downloadCount() {
            return this.shareClassDownloads.length;
        }

        @computed
        get watchListPropertyValue() {
            return get(
                this.props.shareClass,
                `properties_pub.${
                    this.props.dataStore.config.watchListProperty
                }.value`
            );
        }

        @action
        toggleExpanded = () => {
            const { shareClass, dataStore } = this.props;
            dataStore.setExpandStatus(shareClass);
        };

        @action
        toggleWatchList = () => {
            this.props.dataStore.watchList.includes(this.watchListPropertyValue)
                ? this.removeWatchList()
                : this.addWatchList();
        };

        @action
        addWatchList = () => {
            const updatedWatchList = toJS(this.props.dataStore.watchList);
            updatedWatchList.push(this.watchListPropertyValue);
            this.props.dataStore.watchList = updatedWatchList;
        };

        @action
        removeWatchList = () => {
            const updatedWatchList = toJS(this.props.dataStore.watchList);
            this.props.dataStore.watchList = updatedWatchList.filter(
                value => value !== this.watchListPropertyValue
            );
        };

        @computed
        get inWatchList() {
            return this.props.dataStore.watchList.includes(
                this.watchListPropertyValue
            );
        }

        @computed
        get fundCurrency() {
            return get(
                this.props.shareClass,
                `properties_pub.${this.props.dataStore.config
                    .fundCurrencyProperty || 'ccy'}.value`
            );
        }

        @computed
        get locale() {
            return this.props.dataStore.culture;
        }

        @computed
        get fundCardDisclaimers() {
            return this.disclaimerByType(
                this.props.dataStore.config.rowConfig.disclaimers
            );
        }

        @computed
        get noDocumentsDisclaimers() {
            const {
                noDocumentsDisclaimers = [],
            } = this.props.dataStore.config.rowConfig;
            return noDocumentsDisclaimers.length > 0
                ? this.disclaimerByType(noDocumentsDisclaimers)
                : [NO_DOCUMENTS_MESSAGE];
        }

        @action
        disclaimerByType(disclaimerTypes) {
            const { disclaimers = {} } = this.props.shareClass;
            return getDisclaimers(
                this.props.dataStore,
                disclaimerTypes,
                disclaimers
            );
        }

        render() {
            // TODO: Is there a DRYer way to get these as props on to the WrappedComponent?
            // Spreading {...this} doesn't work
            const {
                expanded,
                columns,
                shareClassDownloads,
                downloadCount,
                documentSelected,
                getPriceHistory,
                watchListPropertyValue,
                toggleExpanded,
                toggleWatchList,
                addWatchList,
                removeWatchList,
                inWatchList,
                hasPerformance,
            } = this;

            return (
                <WrappedComponent
                    {...this.props}
                    {...{
                        expanded,
                        columns,
                        shareClassDownloads,
                        downloadCount,
                        documentSelected,
                        getPriceHistory,
                        watchListPropertyValue,
                        toggleExpanded,
                        toggleWatchList,
                        addWatchList,
                        removeWatchList,
                        inWatchList,
                        hasPerformance,
                    }}
                >
                    {this.props.rowType === 'FundCard' && (
                        <FundCard
                            isMobile={this.props.isMobile}
                            shareClass={this.props.shareClass}
                            fundCardDisclaimers={this.fundCardDisclaimers}
                            groupedDocuments={this.groupedDocuments}
                        />
                    )}
                    {this.props.rowType === 'Documents' && (
                        <FundDocuments
                            groupedDocuments={this.groupedDocuments}
                            documentDownloads={this.props.documentDownloads}
                            toggleDownload={this.props.toggleDownload}
                            documentSelected={this.documentSelected}
                            selectedDocumentsLabel={this.selectedDocumentsLabel}
                            availableDocumentsLabel={
                                this.availableDocumentsLabel
                            }
                            fundCardDisclaimers={this.fundCardDisclaimers}
                            noDocumentsDisclaimers={this.noDocumentsDisclaimers}
                        />
                    )}
                </WrappedComponent>
            );
        }
    }

    return Shareclass;
};

export default withShareclass;



// WEBPACK FOOTER //
// ./src/Components/withShareclass.tsx